import { useRef, useState } from "react";

import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import { InboundLayout, InboundHeaderCard } from "ui-components/Layout";
import { InboundDetailAddButton, InboundDetailEditButton } from "ui-components/Form";

import { headerRender, headerValues, headerValidation } from "./co-schema";
import { detailRender, detailValues, detailValidation } from "./co-details-schema";
import CODetail from "./components/CODetail";
import { Success, Error_ } from "ui-components/Alert";
import { InfoDialog } from "ui-components/Alert";
import { skuValidation } from "layouts/views/common/validation";
import { useMutation, useQuery } from "@tanstack/react-query";
import CO from "./components/CO";
import { coColumns } from "./columns";
import { useLocation, useNavigate } from "react-router-dom";
import { getCO } from "services/data.inbound.service";
import { editCO } from "services/data.inbound.service";
import { InboundSaveButton } from "ui-components/Form";
import BasicTable2 from "ui-components/tables/BasicTable2";
import { statusObj, statusLabel } from "layouts/views/common/enum";
import { LoadingDialog } from "ui-components/Alert";
import { cancelDetail, coSkuValidation_, deleteNewDetail, getTotalQuantity, updateDetail } from "../common/common";
import { COCaption } from "../components/Caption";

function EditCO() {
  const location = useLocation();
  const navigate = useNavigate();
  const { coNumber, customerCode } = location.state;
  const [formValues, setFormValues] = useState(headerValues);
  const [alert, setAlert] = useState(null);
  const [details, setDetails] = useState([]);
  const onError = (data) => setAlert(<Error_ data={data} onChange={setAlert} />);
  const EditCOMutation = useMutation(editCO, {});

  const { isFetching } = useQuery({
    queryKey: ["co", { coNumber, customerCode }],
    queryFn: () => getCO(coNumber, customerCode),
    onSuccess: (data) => {
      const { coDetails, status, ...coValues } = data.data.data;
      setFormValues({ ...coValues, status: statusLabel[status] });
      setDetails(coDetails.map((detail) => ({ ...detail, status: statusLabel[detail.status] })));
    },
    onError,
  });

  const form = useForm({
    defaultValues: formValues,
    values: formValues,
    resolver: yupResolver(headerValidation),
  });

  const {
    control,
    watch,
    formState: { errors, isSubmitting },
    setValue,
    getValues,
    trigger,
  } = form;

  const [vendorCode] = watch(["vendorCode"]);

  const headerProps = { headerRender, customerCode, control, setValue, errors, mode: "EDIT" };

  const detailProps = { details, setDetails, values: { customerCode, vendorCode } };

  const onSubmit = async () => {
    window.scrollTo(0, 0);
    const isValid = await trigger();
    if (!isValid) return;

    const data = getValues();

    if (details.length === 0) {
      setAlert(<Error_ content="Invalid CO Details" onChange={setAlert} />);
      return;
    }

    const req = { ...data, coDetails: details.map((d) => ({ ...d, status: statusObj[d.status] })) };

    EditCOMutation.mutate(
      { coNumber, req },
      {
        onSuccess: (data) => navigate("/inbound/co", { state: data.data.message }),
        onError,
      }
    );
  };
  return (
    <InboundLayout lg={12}>
      {isFetching && <LoadingDialog />}
      {/* {(isSubmitting || isSubmitted) && <LoadingDialog />} */}
      {alert}
      <InboundHeaderCard>
        <form noValidate>
          <CO {...headerProps} />
        </form>
      </InboundHeaderCard>

      <Detail {...detailProps} />
      <InboundSaveButton url="/inbound/co" onClick={onSubmit} disabled={isSubmitting} />
    </InboundLayout>
  );
}

const Detail = ({ details, setDetails, values }) => {
  const columns = [
    {
      field: "actions",
      headerName: "Action",
      renderActions: ["Cancel"],
      type: "actions",
    },
    ...coColumns,
  ];
  const [alert, setAlert] = useState(null);

  const [formValues, setFormValues] = useState(null);
  const [dialog, setDialog] = useState(null);
  const lineNo = formValues ? formValues.lineNo : details.length + 1;
  const doRef = useRef(null);
  const returnCodeRef = useRef(null);
  const custNbrRef = useRef(null);
  const expiryDateRef = useRef(null);

  const form = useForm({
    defaultValues: { ...detailValues, lineNo },
    values: formValues || {
      ...detailValues,
      lineNo,
      status: "Open",
      newItem: true,
      doNumber: doRef.current,
      returnCode: returnCodeRef.current,
      custOrdNbr: custNbrRef.current,
      expiryDate: expiryDateRef.current,
    },
    resolver: yupResolver(detailValidation),
  });
  const {
    control,
    watch,
    formState: { errors, isDirty },
    setValue,
    getValues,
    handleSubmit,
    reset,
  } = form;
  const [skuCode] = watch(["skuCode"]);

  const detailProps = {
    detailRender,
    values: { ...values, skuCode },
    control,
    errors,
    setValue,
    getValues,
    mode: "EDIT",
  };
  const onSubmit = (data) => {
    const isSKUDuplicate = coSkuValidation_(details, data);
    if (isSKUDuplicate) {
      setAlert(<Error_ content="Duplicate SKU Code, Customer Order Number, MLN" onChange={setAlert} />);
      return;
    }
    doRef.current = data.doNumber;
    returnCodeRef.current = data.returnCode
    custNbrRef.current = data.custOrdNbr
    expiryDateRef.current = data.expiryDate
    if (formValues === null) {
      setDetails([...details, data]);
      reset();
      setAlert(<Success content="Added" onChange={setAlert} />);
    } else {
      const coDetails = updateDetail(details, data);
      setDetails(coDetails);

      setFormValues(null);
      setAlert(<Success content="Updated" onChange={setAlert} />);
    }
  };
  const onRowClick = (values) => {
    if (formValues !== null && isDirty) {
      setDialog(
        <InfoDialog
          title={`Unsaved changes for Line No ${formValues.lineNo}`}
          content="Do you want to save the changes"
          onOk={() => {
            const data = getValues();
            const recvDetails = details.map((detail) => {
              if (detail.lineNo === formValues.lineNo) return data;
              return detail;
            });
            setDetails(recvDetails);
            setDialog(null);
            setFormValues(values);
          }}
          onClose={() => {
            setDialog(null);
            setFormValues(values);
          }}
        />
      );
    } else {
      setFormValues(values);
    }
    return true;
  };

  const handleDelete = (data) => {
    if (data.newItem === false) return;
    const coDetails = deleteNewDetail(details, data);
    setDetails(coDetails);
    setFormValues(null);
    setAlert(<Success content="Deleted" onChange={setAlert} />);
  };

  const handleCancel = (data) => {
    if (data.newItem === true) return;
    const coDetails = cancelDetail(details, data);
    setDetails(coDetails);
    setFormValues(null);
    setAlert(<Success content="Cancelled" onChange={setAlert} />);
  };
  const totalQuantity = getTotalQuantity(details, "coQty");
  const Caption = <COCaption totalRows={details.length} totalQuantity={totalQuantity} />;
  return (
    <InboundHeaderCard>
      {alert}
      {dialog}
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <CODetail {...detailProps} />
        {formValues === null ? (
          <InboundDetailAddButton />
        ) : (
          <InboundDetailEditButton
            disabled={formValues.status === "Cancel" || !isDirty}
            onClick={() => setFormValues(null)}
          />
        )}
      </form>
      <BasicTable2
        columns={columns}
        caption={Caption}
        rows={details}
        onRowClick={onRowClick}
        onDelete={handleDelete}
        onCancel={handleCancel}
        mode="EDIT"
      />
    </InboundHeaderCard>
  );
};

export default EditCO;
